import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef(null);
  const iframeRef = useRef(null);

  const GOOGLE_FORM_ACTION_URL = "https://docs.google.com/forms/u/0/d/e/1FAIpQLSfws2swqT4oaVr_vUIF3oungCBJnZ6KQTEt2lC_y4-yCBD_aA/formResponse";

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // We can't await a cross-origin form submission result directly due to CORS/browser security with iframes.
    // So we assume success if no error is thrown immediately and set a timeout to reset the UI.
    // The actual submission happens via the form's action + target=iframe.
    
    if (formRef.current) {
        formRef.current.submit();
    }

    setTimeout(() => {
        setIsSubmitting(false);
        setIsSuccess(true);
        toast.success("Message sent successfully!");
        if (formRef.current) formRef.current.reset();
        setTimeout(() => setIsSuccess(false), 3000);
    }, 1500); // Artificial delay to simulate processing time before showing success
  };

  return (
    <Card className="border-0 shadow-2xl bg-white overflow-hidden relative">
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
      <CardHeader className="pb-6">
        <CardTitle className="text-2xl font-bold text-gray-900">Send us a Message</CardTitle>
        <CardDescription className="text-base text-gray-500">
          Fill out the form below and we'll get back to you within 24 hours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Hidden Iframe for silent submission */}
        <iframe 
            name="hidden_iframe" 
            id="hidden_iframe" 
            style={{ display: 'none' }} 
            ref={iframeRef}
            title="submission-frame"
        ></iframe>

        <form 
            ref={formRef}
            action={GOOGLE_FORM_ACTION_URL}
            method="POST"
            target="hidden_iframe"
            onSubmit={handleSubmit} 
            className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="entry.288970735" className="text-gray-700">Name</Label>
              <Input
                name="entry.288970735"
                id="entry.288970735"
                placeholder="John Doe"
                required
                className="bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="entry.487229735" className="text-gray-700">Phone</Label>
              <Input
                name="entry.487229735"
                id="entry.487229735"
                type="tel"
                placeholder="+91 98765 43210"
                required
                className="bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all h-11"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="entry.466113014" className="text-gray-700">Email</Label>
            <Input
              name="entry.466113014"
              id="entry.466113014"
              type="email"
              placeholder="john@example.com"
              required
              className="bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="entry.1882947240" className="text-gray-700">Message</Label>
            <Textarea
              name="entry.1882947240"
              id="entry.1882947240"
              placeholder="How can we help you?"
              rows={5}
              required
              className="bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all resize-none"
            />
          </div>

          <Button 
            type="submit" 
            className={`w-full h-12 text-lg font-semibold transition-all duration-300 ${isSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800'}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
            ) : isSuccess ? (
              <span className="flex items-center justify-center">
                <CheckCircle2 className="mr-2" /> Message Sent!
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Send Message <Send className="ml-2 w-4 h-4" />
              </span>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
